import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useTranslation } from '../i18n/I18nContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Plus, Edit, Share2, BarChart3, Trash2, LogOut, Eye, EyeOff, Settings, ChevronDown, MapPin, Users } from 'lucide-react';
import { toast } from 'sonner';
import LanguageSwitcher from '../components/LanguageSwitcher';
import CreateVillageModal from '../components/admin/CreateVillageModal';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

// Feature flag for Unified Client Admin
const ENABLE_UNIFIED_CLIENT_ADMIN = process.env.REACT_APP_ENABLE_UNIFIED_CLIENT_ADMIN === 'true';

export default function Dashboard() {
  const { t } = useTranslation();
  const { user, logout, isSuperAdmin } = useAuth();
  const { canWrite, canPublish, canDelete, canShare } = usePermissions();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unpublishDialog, setUnpublishDialog] = useState({ open: false, course: null });
  const [showCreateVillage, setShowCreateVillage] = useState(false);
  const [villages, setVillages] = useState([]);
  const [selectedVillageId, setSelectedVillageId] = useState(() => localStorage.getItem('qoldo_selected_village') || null);
  const [showVillageDropdown, setShowVillageDropdown] = useState(false);

  const selectedVillage = villages.find(v => v.id === selectedVillageId) || villages[0] || null;

  const selectVillage = (villageId) => {
    setSelectedVillageId(villageId);
    localStorage.setItem('qoldo_selected_village', villageId);
  };

  const fetchCourses = async () => {
    try {
      const response = await api.get('/courses');
      setCourses(response.data);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
      toast.error('Could not load courses');
    } finally {
      setLoading(false);
    }
  };

  const fetchVillages = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/unified-clients`);
      if (response.ok) {
        const data = await response.json();
        setVillages(data.unified_clients || []);
      }
    } catch (error) {
      console.error('Failed to fetch villages:', error);
    }
  };

  useEffect(() => {
    fetchCourses();
    if (ENABLE_UNIFIED_CLIENT_ADMIN) {
      fetchVillages();
    }
  }, []);

  const handleCreateCourse = async () => {
    try {
      const response = await api.post('/courses', {
        title: t('editor_title_new'),
        description: '',
        blocks: []
      });
      navigate(`/editor/${response.data.id}`);
    } catch (error) {
      console.error('Failed to create course:', error);
      toast.error('Could not create course');
    }
  };

  const handleDelete = async (courseId) => {
    if (!window.confirm(t('dashboard_delete_confirm'))) {
      return;
    }
    try {
      await api.delete(`/courses/${courseId}`);
      setCourses(courses.filter(c => c.id !== courseId));
      toast.success('Course deleted');
    } catch (error) {
      console.error('Failed to delete course:', error);
      toast.error('Could not delete course');
    }
  };

  const handleUnpublish = async (course) => {
    try {
      await api.put(`/courses/${course.id}`, {
        ...course,
        status: 'unpublished'
      });
      setCourses(courses.map(c => 
        c.id === course.id ? { ...c, status: 'unpublished' } : c
      ));
      toast.success('Training offline');
      setUnpublishDialog({ open: false, course: null });
    } catch (error) {
      console.error('Failed to unpublish course:', error);
      toast.error('Could not unpublish');
    }
  };

  const handlePublishAgain = async (course) => {
    try {
      await api.put(`/courses/${course.id}`, {
        ...course,
        status: 'published'
      });
      setCourses(courses.map(c => 
        c.id === course.id ? { ...c, status: 'published' } : c
      ));
      toast.success('Training published');
    } catch (error) {
      console.error('Failed to publish course:', error);
      toast.error('Could not publish');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'published':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
            <Eye className="h-3 w-3" />
            {t('dashboard_status_published')}
          </span>
        );
      case 'unpublished':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
            <EyeOff className="h-3 w-3" />
            {t('dashboard_status_unpublished')}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
            {t('dashboard_status_draft')}
          </span>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Qoldo</h1>
            <p className="text-xs text-gray-500">{t('app_tagline')}</p>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            {isSuperAdmin && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin/users')}
                data-testid="nav-users-button"
                title="Gebruikersbeheer"
              >
                <Users className="h-4 w-4 mr-1" /> Gebruikers
              </Button>
            )}
            <span className="text-sm text-gray-600 hidden sm:inline">{user?.email || user?.username}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Title + Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-semibold">{t('dashboard_title')}</h2>
            <p className="text-gray-600 text-sm">{t('dashboard_subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            {ENABLE_UNIFIED_CLIENT_ADMIN && (
              <>
                {/* Village context selector */}
                <div className="relative">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowVillageDropdown(!showVillageDropdown)}
                    data-testid="village-switcher-button"
                  >
                    <MapPin className="h-4 w-4 mr-2" />
                    <span className="text-xs text-gray-500 mr-1">Village:</span>
                    <span className="font-medium">{selectedVillage ? selectedVillage.name : 'None'}</span>
                    <ChevronDown className="ml-2 h-3 w-3" />
                  </Button>
                  {showVillageDropdown && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setShowVillageDropdown(false)}
                      />
                      <div
                        className="absolute left-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
                        data-testid="village-dropdown"
                      >
                        <div className="p-2 border-b border-gray-100">
                          <p className="text-xs text-gray-500 font-medium uppercase">Villages</p>
                        </div>
                        {villages.length > 0 && (
                          <div className="max-h-60 overflow-y-auto">
                            {villages.map(village => (
                              <button
                                key={village.id}
                                onClick={() => {
                                  selectVillage(village.id);
                                  setShowVillageDropdown(false);
                                  navigate(`/admin/unified-clients/${village.id}`);
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between"
                                data-testid={`village-link-${village.id}`}
                              >
                                <span className="text-sm font-medium text-gray-900">{village.name}</span>
                                <span className="text-xs text-gray-400">{village.id}</span>
                              </button>
                            ))}
                          </div>
                        )}
                        {villages.length === 0 && (
                          <div className="px-3 py-2 text-sm text-gray-500">No villages yet</div>
                        )}
                        <div className="border-t border-gray-100">
                          {canWrite && (
                            <button
                              onClick={() => {
                                setShowVillageDropdown(false);
                                setShowCreateVillage(true);
                              }}
                              className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-2 text-blue-600 font-medium text-sm"
                              data-testid="create-village-dropdown-item"
                            >
                              <Plus className="h-4 w-4" />
                              Create new village
                            </button>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
                {/* Store Management — direct action */}
                {canWrite && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (selectedVillage) {
                        navigate(`/admin/unified-clients/${selectedVillage.id}`);
                      } else {
                        toast.error('No villages available');
                      }
                    }}
                    data-testid="store-management-button"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Store Management
                  </Button>
                )}
              </>
            )}
            {canWrite && (
              <Button onClick={handleCreateCourse} data-testid="dashboard-create-course-button">
                <Plus className="h-4 w-4 mr-2" />
                {t('dashboard_new_course')}
              </Button>
            )}
          </div>
        </div>

        {/* Course List */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">{t('loading')}</div>
        ) : courses.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-gray-500 mb-4">{t('dashboard_no_courses')}</p>
            {canWrite && (
              <Button onClick={handleCreateCourse} data-testid="dashboard-create-course-empty-button">
                <Plus className="h-4 w-4 mr-2" />
                {t('dashboard_create_course')}
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid gap-4">
            {courses.map((course) => (
              <Card key={course.id} className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold truncate">{course.title || t('editor_title_new')}</h3>
                      {getStatusBadge(course.status)}
                    </div>
                    <p className="text-sm text-gray-500">
                      {course.blocks_count ?? course.blocks?.length ?? 0} {t('dashboard_blocks')}
                    </p>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/editor/${course.id}`)}
                      data-testid={`dashboard-edit-course-${course.id}`}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      {canWrite ? t('dashboard_edit') : t('dashboard_view')}
                    </Button>
                    
                    {course.status === 'published' && (
                      <>
                        {canShare && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/share/${course.id}`)}
                            data-testid={`dashboard-share-course-${course.id}`}
                          >
                            <Share2 className="h-4 w-4 mr-1" />
                            {t('dashboard_share')}
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/analytics/${course.id}`)}
                          data-testid={`dashboard-stats-course-${course.id}`}
                        >
                          <BarChart3 className="h-4 w-4 mr-1" />
                          {t('dashboard_stats')}
                        </Button>
                        {canPublish && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setUnpublishDialog({ open: true, course })}
                            className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                            data-testid={`dashboard-unpublish-course-${course.id}`}
                          >
                            <EyeOff className="h-4 w-4 mr-1" />
                            {t('dashboard_unpublish')}
                          </Button>
                        )}
                      </>
                    )}
                    
                    {course.status === 'unpublished' && canPublish && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePublishAgain(course)}
                        className="text-green-600 hover:text-green-700"
                        data-testid={`dashboard-publish-again-course-${course.id}`}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        {t('dashboard_publish_again')}
                      </Button>
                    )}
                    
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(course.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`dashboard-delete-course-${course.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Unpublish Dialog */}
      <Dialog open={unpublishDialog.open} onOpenChange={(open) => setUnpublishDialog({ open, course: unpublishDialog.course })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('dashboard_unpublish_title')}</DialogTitle>
            <DialogDescription>
              {t('dashboard_unpublish_confirm', { title: unpublishDialog.course?.title || '' })}
              <br /><br />
              {t('dashboard_unpublish_info')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnpublishDialog({ open: false, course: null })}>
              {t('dashboard_cancel')}
            </Button>
            <Button variant="destructive" onClick={() => handleUnpublish(unpublishDialog.course)}>
              {t('dashboard_confirm_unpublish')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Village Modal */}
      {showCreateVillage && (
        <CreateVillageModal
          onSave={(result) => {
            setShowCreateVillage(false);
            toast.success(`Village "${result.village.name}" created`);
            // Refresh villages list
            fetchVillages();
            // Navigate to the new village admin page
            navigate(`/admin/unified-clients/${result.village.id}`);
          }}
          onClose={() => setShowCreateVillage(false)}
        />
      )}
    </div>
  );
}
